import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { searchApi } from '@/api/search';
import { extractApiError } from '@/api/client';

// Debounce interval for keystrokes → server. Short enough to feel
// responsive, long enough to avoid burning the 30 req/min throttle on
// the `/search` endpoint.
const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;

const LISTBOX_ID = 'global-search-listbox';
const optionId = (index: number): string => `global-search-option-${index}`;

/**
 * @param accent 지금 화면이 속한 사이드바 묶음 색(TopBar 가 넘긴다) — 단축키 배지를 그 색으로 물들인다
 *   (2026-10-23 오너 "검색창 버튼도 탭별 색에 맞게"). 없으면(홈 등) 회색 키캡.
 */
export function GlobalSearchBar({ accent }: { accent?: string } = {}) {
  const navigate = useNavigate();
  const [raw, setRaw] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  // 결정 ⑦ 후속 — 키보드 이동용 커서. -1 = 아무것도 선택 안 됨(첫 ↓ 가 0번으로).
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Debounce the raw value.
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(raw), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [raw]);

  // Cmd/Ctrl+K focuses the search box. Escape clears + blurs.
  useEffect(() => {
    const onGlobalKey = (e: globalThis.KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onGlobalKey);
    return () => window.removeEventListener('keydown', onGlobalKey);
  }, []);

  // Click outside → close dropdown.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const hasQuery = debounced.trim().length >= MIN_QUERY_LENGTH;

  const q = useQuery({
    queryKey: ['global-search', debounced],
    queryFn: () => searchApi.search(debounced),
    enabled: hasQuery,
    staleTime: 10_000,
  });

  const results = q.data;

  // 키보드 이동은 섹션(로봇/고객사)을 가로지르므로, 렌더 순서와 동일한
  // 평면 배열을 따로 만들어 인덱스를 매긴다.
  const flatTargets = useMemo(
    () => [
      ...(results?.robots ?? []).map((r) => `/robots/${r.id}`),
      ...(results?.customers ?? []).map((c) => `/customers/${c.id}`),
    ],
    [results],
  );
  const total = flatTargets.length;
  const robotCount = results?.robots.length ?? 0;

  // 결과가 바뀌면 이전 커서는 다른 항목을 가리키게 되므로 되돌린다.
  useEffect(() => {
    setActiveIndex(-1);
  }, [debounced]);

  function go(to: string): void {
    navigate(to);
    setOpen(false);
    setRaw('');
    setDebounced('');
    setActiveIndex(-1);
    inputRef.current?.blur();
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Escape') {
      setOpen(false);
      setRaw('');
      setActiveIndex(-1);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (total === 0) return;
      // 브라우저 기본 캐럿 이동(입력 끝/앞으로 점프)을 막아야 목록 이동만 남는다.
      e.preventDefault();
      setOpen(true);
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((prev) => {
        // 아직 아무것도 안 고른 상태(-1)에서 ↑ 는 마지막 항목으로 감싼다.
        if (prev < 0) return delta === 1 ? 0 : total - 1;
        return (prev + delta + total) % total;
      });
      return;
    }
    if (e.key === 'Enter') {
      const to = flatTargets[activeIndex];
      if (to) {
        e.preventDefault();
        go(to);
      }
    }
  }

  return (
    <div ref={wrapRef} style={wrap}>
      <input
        ref={inputRef}
        value={raw}
        onChange={(e) => {
          setRaw(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="로봇이나 고객사 검색…"
        className="kuun-topbar-search"
        style={inputStyle}
        role="combobox"
        aria-label="전역 검색"
        aria-expanded={open}
        aria-controls={LISTBOX_ID}
        aria-autocomplete="list"
        aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
      />
      <span
        style={accent ? { ...shortcut, ...accentKey(accent) } : shortcut}
        data-testid="search-shortcut"
      >
        {IS_MAC ? '⌘K' : 'Ctrl+K'}
      </span>

      {open && (
        <div style={dropdown} id={LISTBOX_ID} role="listbox" aria-label="검색 결과">
          {!hasQuery ? (
            <div style={empty}>두 글자 이상 입력해 주세요.</div>
          ) : q.isLoading ? (
            <div style={empty}>검색 중…</div>
          ) : q.error ? (
            <div style={{ ...empty, color: 'var(--fg-danger)' }}>{extractApiError(q.error)}</div>
          ) : !results || total === 0 ? (
            <div style={empty}>결과가 없습니다.</div>
          ) : (
            <>
              {results.robots.length > 0 && (
                <Section title="로봇" count={results.robots.length}>
                  {results.robots.map((r, i) => (
                    <SearchItem
                      key={r.id}
                      index={i}
                      active={activeIndex === i}
                      primary={r.serialShort ? `#${r.serialShort}` : (r.serialLong ?? '—')}
                      secondary={r.modelDisplay}
                      onClick={() => go(`/robots/${r.id}`)}
                    />
                  ))}
                </Section>
              )}
              {results.customers.length > 0 && (
                <Section title="고객사" count={results.customers.length}>
                  {results.customers.map((c, i) => (
                    <SearchItem
                      key={c.id}
                      index={robotCount + i}
                      active={activeIndex === robotCount + i}
                      primary={c.displayName}
                      badge={c.retired ? '종료' : undefined}
                      secondary={customerContext(c.addressLine, c.salesOwnerCode) ?? c.legalName}
                      onClick={() => go(`/customers/${c.id}`)}
                    />
                  ))}
                </Section>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// 동명 고객사가 흔해 상호만으론 구분이 안 된다. 주소 앞 두 토막(시/구)과
// 영업 담당 코드를 붙여 준다. 둘 다 비면 호출부가 legalName 으로 fallback.
function customerContext(addressLine: string | null, salesOwnerCode: string | null): string | null {
  const region = addressLine?.trim().split(/\s+/).slice(0, 2).join(' ');
  const parts = [region, salesOwnerCode?.trim()].filter((v): v is string => !!v);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <div>
      <div style={sectionHead}>
        {title}
        <span style={{ color: 'var(--fg-muted)', marginLeft: 6 }}>({count})</span>
      </div>
      {children}
    </div>
  );
}

function SearchItem({
  index,
  active,
  primary,
  secondary,
  badge,
  onClick,
}: {
  index: number;
  active: boolean;
  primary: string;
  secondary?: string;
  badge?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      id={optionId(index)}
      role="option"
      aria-selected={active}
      onClick={onClick}
      style={active ? { ...itemStyle, ...itemActiveStyle } : itemStyle}
      onMouseDown={(e) => e.preventDefault()} // keep input focus for keyboard flow
    >
      <div style={{ fontSize: 13, fontWeight: 500 }}>
        {primary}
        {badge && <span style={badgeStyle}>{badge}</span>}
      </div>
      {secondary && <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{secondary}</div>}
    </button>
  );
}

// round5 #11 — 단축키 리스너는 metaKey/ctrlKey 양쪽을 받지만(onKey) 배지가
// Mac 기호 고정이었음. Windows 운영자에겐 Ctrl+K 로 표기. (vite SPA 라
// navigator 접근 안전.)
const IS_MAC = /Mac|iPhone|iPad/i.test(
  typeof navigator !== 'undefined' ? navigator.platform || navigator.userAgent : '',
);

const wrap: CSSProperties = { position: 'relative', width: 320 };

const inputStyle: CSSProperties = {
  width: '100%',
  height: 32,
  // 우측 패딩은 배지 폭에 맞춤 — Ctrl+K(6자)가 ⌘K(2자)보다 넓다.
  padding: IS_MAC ? '0 44px 0 12px' : '0 60px 0 12px',
  border: '1px solid var(--border-default)',
  borderRadius: 4,
  fontSize: 12,
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontFamily: 'inherit',
};

// 2026-10-21 오너 — 단축키 배지가 검색칸과 같은 흰 바탕이라 칸 일부처럼 보였다. 회색 키캡(아래 테두리 두껍게)으로.
const shortcut: CSSProperties = {
  position: 'absolute',
  right: 7,
  top: 6,
  fontSize: 10,
  fontWeight: 600,
  fontFamily: 'var(--font-mono)',
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  padding: '1px 6px',
  borderRadius: 4,
  border: '1px solid var(--border-strong)',
  borderBottomWidth: 2,
  pointerEvents: 'none',
};

/** 묶음 색 키캡 — 옅은 묶음 색 바탕 · 짙은 묶음 색 글자 · 테두리 */
const accentKey = (accent: string): CSSProperties => ({
  color: `color-mix(in srgb, ${accent} 80%, #000)`,
  background: `color-mix(in srgb, ${accent} 14%, var(--bg-surface))`,
  border: `1px solid color-mix(in srgb, ${accent} 45%, var(--bg-surface))`,
  borderBottomWidth: 2,
});

const dropdown: CSSProperties = {
  position: 'absolute',
  top: 38,
  left: 0,
  right: 0,
  background: 'var(--bg-surface)',
  // 상단 바가 묶음 색 + 흰 글자가 된 뒤(2026-10-21) 결과 이름(primary)이 흰색을 물려받아 안 보였다
  // (2026-10-22 오너 "검색창에도 지점명이 안 보여"). 창 안 글자색을 여기서 끊는다.
  color: 'var(--fg-default)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  boxShadow: 'var(--shadow-lg)',
  maxHeight: 480,
  overflow: 'auto',
  zIndex: 20,
};

const empty: CSSProperties = {
  padding: '14px 14px',
  color: 'var(--fg-muted)',
  fontSize: 12,
};

const sectionHead: CSSProperties = {
  padding: '8px 12px 4px',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
  background: 'var(--bg-sunken)',
  borderTop: '1px solid var(--border-subtle)',
};

const itemStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '8px 12px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderBottom: '1px solid var(--border-subtle)',
};

const itemActiveStyle: CSSProperties = {
  background: 'var(--bg-selected)',
};

const badgeStyle: CSSProperties = {
  marginLeft: 6,
  padding: '1px 5px',
  borderRadius: 3,
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  border: '1px solid var(--border-default)',
};
